import type { CourseHoleInfo } from '@moment-golf/types';

/**
 * Minimal shape of a score row needed for playoff resolution.
 * Matches RoundScoreRow in Scoring.tsx — hole number keys hold the player's
 * gross stroke count on that hole.
 */
export interface PlayoffScoreRow {
    _id: string;
    playerName: string;
    division: string;
    flight: string;
    scoreToPar: number;
    total: number;
    thru: number;
    confirmed: boolean;
    submitted: boolean;
    widthdrawn: boolean;
    disqualified: boolean;
    indPlayoffWin: boolean;
    teamPlayoffWin: boolean;
    finalRank: { gross: number; net: number } | number;
    finalTeamRank: { gross: number; net: number } | number;
    [key: number]: number | undefined;
}

export interface PlayoffResult {
    /** The original score row */
    row: PlayoffScoreRow;
    /** 1-based rank within this tied group (lower is better) */
    playoffRank: number;
    /** True only for the player(s) at rank 1 */
    isWinner: boolean;
    /**
     * The hole number (1-based) whose score first separated this player
     * from the group above them. null when the tie could not be broken.
     */
    decidingHole: number | null;
}

export interface TiedGroup {
    division: string;
    /** Empty string when no flight data exists */
    flight: string;
    scoreToPar: number;
    players: PlayoffScoreRow[];
    results: PlayoffResult[];
    /**
     * The rank these players all currently occupy on the leaderboard —
     * i.e. count of players in the same div/flight with scoreToPar strictly
     * lower than this group's score, plus 1.
     */
    baseRank: number;
}

/**
 * Resolves a group of tied players using score-card tiebreaker:
 * holes are compared in ascending hdcp order (hdcp=1 is the hardest, checked first).
 *
 * Handles 3+ player ties by recursively splitting sub-groups at each hole
 * until all ties are either broken or exhausted.
 *
 * @param players  Two or more tied RoundScoreRows for the same scoreToPar value.
 * @param holes    Full `roundHoles` array from Scoring.tsx (CourseHoleInfo[]).
 * @returns        PlayoffResult[] sorted by playoffRank ascending.
 */
export function resolvePlayoffTies(
    players: PlayoffScoreRow[],
    holes: CourseHoleInfo[],
): PlayoffResult[] {
    // Sort holes by hdcp ascending (1 = hardest, resolved first).
    // Use hole number as tiebreaker so natural order applies when hdcps are equal or all 0.
    const holesInOrder = [...holes].sort(
        (a, b) => (a.hdcp ?? 999) - (b.hdcp ?? 999) || a.number - b.number,
    );

    // Each entry is a group of still-tied players plus the decidingHole that
    // separated them from the previous group (null for the first group).
    type ScoredGroup = { players: PlayoffScoreRow[]; decidingHole: number | null };

    let groups: ScoredGroup[] = [{ players, decidingHole: null }];

    for (const hole of holesInOrder) {
        const newGroups: ScoredGroup[] = [];

        for (const group of groups) {
            if (group.players.length <= 1) {
                newGroups.push(group);
                continue;
            }

            // Map each player to their score on this hole (0 = not played / missing)
            const byScore = new Map<number, PlayoffScoreRow[]>();
            for (const player of group.players) {
                const score = (player[hole.number] as number | undefined) ?? 0;
                if (!byScore.has(score)) byScore.set(score, []);
                byScore.get(score)!.push(player);
            }

            const sortedScores = Array.from(byScore.keys()).sort((a, b) => a - b);

            if (sortedScores.length === 1) {
                // All tied on this hole — keep group intact, try next hole
                newGroups.push(group);
                continue;
            }

            // Hole broke the tie — first sub-group inherits the parent's decidingHole
            // (it was already separated before), subsequent sub-groups get THIS hole.
            let first = true;
            for (const score of sortedScores) {
                newGroups.push({
                    players: byScore.get(score)!,
                    decidingHole: first ? group.decidingHole : hole.number,
                });
                first = false;
            }
        }

        groups = newGroups;
        if (groups.every((g) => g.players.length <= 1)) break;
    }

    // Assign final ranks. Players in the same (unresolved) sub-group share a rank.
    const results: PlayoffResult[] = [];
    let rank = 1;

    for (const group of groups) {
        for (const player of group.players) {
            results.push({
                row: player,
                playoffRank: rank,
                isWinner: rank === 1,
                decidingHole: group.decidingHole,
            });
        }
        rank += group.players.length;
    }

    results.sort((a, b) => a.playoffRank - b.playoffRank);
    return results;
}

/**
 * Groups an array of score rows into TiedGroup records.
 *
 * Grouping: division → flight → scoreToPar (flight ignored when hasFlights is false).
 * Only groups with 2+ players (genuine ties) are returned.
 */
export interface BuildTiedGroupsOptions {
    /** When false, flight strings on rows are normalized to '' so registration data cannot split buckets. */
    hasFlights?: boolean;
    /** When true, baseRank counts peers in the full division, not the division+flight slice. */
    divisionWideBaseRank?: boolean;
}

export function buildTiedGroups(
    rows: PlayoffScoreRow[],
    holes: CourseHoleInfo[],
    options: BuildTiedGroupsOptions = {},
): TiedGroup[] {
    const hasFlights = options.hasFlights ?? true;
    const divisionWideBaseRank = options.divisionWideBaseRank ?? false;

    // Key: `${division}||${flight}`
    const divFlightMap = new Map<string, PlayoffScoreRow[]>();

    for (const row of rows) {
        const flight = hasFlights ? (row.flight ?? '') : '';
        const key = `${row.division}||${flight}`;
        if (!divFlightMap.has(key)) divFlightMap.set(key, []);
        divFlightMap.get(key)!.push(row);
    }

    const tiedGroups: TiedGroup[] = [];

    Array.from(divFlightMap.entries()).forEach(([key, groupRows]) => {
        const [division, flight] = key.split('||');

        // Build a map of scoreToPar → players within this div/flight
        const scoreMap = new Map<number, PlayoffScoreRow[]>();
        for (const row of groupRows) {
            const s = row.scoreToPar;
            if (!scoreMap.has(s)) scoreMap.set(s, []);
            scoreMap.get(s)!.push(row);
        }

        const rankScopeRows = divisionWideBaseRank
            ? rows.filter((r) => r.division === division)
            : groupRows;

        Array.from(scoreMap.entries()).forEach(([scoreToPar, players]) => {
            if (players.length < 2) return; // no tie

            const baseRank =
                rankScopeRows.filter((r: PlayoffScoreRow) => r.scoreToPar < scoreToPar).length + 1;

            tiedGroups.push({
                division,
                flight: hasFlights ? flight : '',
                scoreToPar,
                players,
                results: resolvePlayoffTies(players, holes),
                baseRank,
            });
        });
    });

    // Sort groups: division asc → flight asc → scoreToPar asc
    tiedGroups.sort((a, b) => {
        const divCmp = a.division.localeCompare(b.division);
        if (divCmp !== 0) return divCmp;
        const flightCmp = a.flight.localeCompare(b.flight);
        if (flightCmp !== 0) return flightCmp;
        return a.scoreToPar - b.scoreToPar;
    });

    return tiedGroups;
}
